/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { StockData } from '../types/stock';
import { formatNumberWithCommas, formatPrice, isNotNullUndefined } from '../helpers/common';
import { useNavigate } from 'react-router-dom';
import { Select, Pagination, DatePicker, Space, Modal } from 'antd';
import { DownCircleOutlined, EyeOutlined, LeftCircleOutlined, MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import Images from '../assets/images';
import { useFetchArticleContent, useFetchArticleStockRecommendations, useFetchParadigmStocks } from '../services/paradigm.service';
import { ArticleStockRecommendationData } from '../types/paradigm';
import { storage } from '../utils/storage';
import Search from 'antd/es/input/Search';
import { IResponse, IResponseModel } from '../types/response';
import React from 'react';
import dayjs from 'dayjs';

const ParadigmEvents = () => {
    const navigate = useNavigate();
    const [articleStockRecommendationData, setArticleStockRecommendationData] = useState<ArticleStockRecommendationData[]>([]);
    const preservedArticleStockRecommendationData = useRef<ArticleStockRecommendationData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [totalItems, setTotalItems] = useState<number>(0);
    const isAuthenticated = useRef(false);
    const isGuestUser = useRef(false);
    const [stockSymbol, setStockSymbol] = useState<string>("");
    const [stocks, setStocksList] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const defaultPageSize = 100;
    const [pageSize, setPageSize] = useState<number>(defaultPageSize);
    const [dateRange, setDateRange] = useState(["", ""]);
    const [stockSymbolOpen, setStockSymbolOpen] = useState(false);
    const { RangePicker } = DatePicker;
    const [expandedRowIndex, setExpandedRowIndex] = useState(null);
    const [selectedTradingType, setSelectedTradingType] = useState('spot');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [articleContent, setArticleContent] = useState<string>('');
    const token = useRef("");
    const [showSignInPopup, setShowSignInPopup] = useState(false);
    const [scrollHeight, setScrollHeight] = useState("");
    const isSyncInProgress = useRef(false);

    const getParadigmStocksList = useFetchParadigmStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setStocksList(options);
            }
            else {
                setStocksList([]);
            }
        },
    });

    const getArticleStockRecommendationsDetails = useFetchArticleStockRecommendations({
        onSuccess: (response: IResponseModel<ArticleStockRecommendationData>) => {
            if (response.statusCode === 200) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                preservedArticleStockRecommendationData.current = formattedData;
                if (response.data.length > 0 && isNotNullUndefined(response.data[0].recordCount)) {
                    setArticleStockRecommendationData(formattedData);
                    setTotalItems(response.data[0].recordCount);
                } else {
                    resetPagination();
                }
                setLoading(false);
            }
            else {
                resetPagination();
                setLoading(false);
            }
            isSyncInProgress.current = false;
        },
    });

    const resetPagination = () => {
        setArticleStockRecommendationData([]);
        setCurrentPage(1);
        setPageSize(defaultPageSize);
        setTotalItems(0);
    };

    const prepareArticleStockRecommendationDataParams = (page: number, pageSize: number) => ({
        stockSymbols: stockSymbol,
        eventDateFrom: dateRange[0],
        eventDateTo: dateRange[1],
        isGuestUser: isGuestUser.current,
        pageNumber: page,
        pageSize: pageSize
    });


    const fetchArticleStockRecommendationData = () => {
        setLoading(true);
        setArticleStockRecommendationData([]);
        const requestParams = prepareArticleStockRecommendationDataParams(currentPage, pageSize);
        getArticleStockRecommendationsDetails.mutate(requestParams);
    };

    const fetchParadigmStocksList = () => {
        getParadigmStocksList.mutate({});
    }

    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleDateChange = (_: any, dateStrings: any) => {
        resetPagination();
        setDateRange(dateStrings);
    }

    const handleStockSymbolChange = (value: any) => {
        resetPagination();
        setStockSymbol(value.join(','));
        if (value.length >= 1) {
            setStockSymbolOpen(false);
        }
    };

    const onRefresh = () => {
        setLoading(true);
        fetchArticleStockRecommendationData();
    }

    const onSearchContractName = (event: any) => {
        const searchValue = event.target.value.trim().toLowerCase();
        if (searchValue.length >= 2) {
            const filteredData = preservedArticleStockRecommendationData.current.filter(x => x.contractName.toLowerCase().includes(searchValue));
            setArticleStockRecommendationData(filteredData);
        } else {
            setArticleStockRecommendationData(preservedArticleStockRecommendationData.current);
        }
    }

    const syncAuthAndFetchData = () => {
        if (isSyncInProgress.current) {
            return;
        }
        isSyncInProgress.current = true;

        const [isAuth, isGuest] = ["isAuthenticated", "isGuestUser"].map(key =>
            JSON.parse(storage.getLocalProperty(key) ?? "false")
        );

        isAuthenticated.current = isAuth;
        isGuestUser.current = isGuest;
        token.current = (isGuest || isAuth) ? storage.getToken() : "";
        if (token.current !== "") {
            fetchParadigmStocksList();
            isSyncInProgress.current = false;
        } else {
            setTimeout(() => {
                isSyncInProgress.current = false;
                syncAuthAndFetchData();
            }, 100);
        }
    };

    useEffect(() => {
        if (token.current !== "" && !isSyncInProgress.current) {
            isSyncInProgress.current = true;
            fetchArticleStockRecommendationData();
        } else {
            syncAuthAndFetchData();
        }
    }, [stockSymbol, dateRange, currentPage, pageSize, token.current]); // Runs only when necessary

    useEffect(() => {
        window.addEventListener("authStateChanged", syncAuthAndFetchData);
        return () => {
            window.removeEventListener("authStateChanged", syncAuthAndFetchData);
        };
    }, []); // Ensures listener is added only once

    const handleOnClickArticleContent = (articleId: string) => {
        getArticleContent.mutate({
            articleId: articleId
        });
        setIsModalVisible(true);
    };

    const getArticleContent = useFetchArticleContent({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                const content = response.data.articleContent;
                setArticleContent(content);
            }
            else {
                setArticleContent('');
            }
        },
    })

    const closeModal = () => {
        setIsModalVisible(false);
        setArticleContent('');
    };

    /* Collaps Grid Changes Start */
    const handleTradingTypeChange = (type: string) => {
        setSelectedTradingType(type);
        // if (type === 'spot') {
        //     setOptionTradeSelectedType('yahoo');
        // }
    };

    // const handleOptionTradeChartChange = (type: string) => {
    //     setSelectedTradingType('option');
    //     setOptionTradeSelectedType(type);
    // };

    const handleRowClick = (index: any) => {
        setExpandedRowIndex(expandedRowIndex === index ? null : index);
        setSelectedTradingType('spot');
        isGuestUser.current = JSON.parse(storage.getLocalProperty("isGuestUser") ?? false);
        isAuthenticated.current = JSON.parse(storage.getLocalProperty("isAuthenticated") ?? false);
        if (isGuestUser.current) {
            (expandedRowIndex !== index) ?
                setShowSignInPopup(!false)
                : setShowSignInPopup(!showSignInPopup)
        }
    };

    const TradingViewChart = ({ symbol }: any) => {
        const containerRef = useRef<HTMLDivElement | null>(null);

        useEffect(() => {
            if (containerRef.current) {
                const script = document.createElement('script');
                script.src = 'https://s3.tradingview.com/tv.js';
                script.async = true;


                script.onload = () => {
                    new window.TradingView.widget({
                        symbol: symbol || 'SAVA',
                        interval: 'D',
                        width: '100%',
                        height: '500',
                        container_id: containerRef.current!.id,
                        allow_symbol_change: true,
                        hide_top_toolbar: true,
                        hide_legend: true,
                    });
                };
                document.head.appendChild(script);
            }
        }, [symbol]);

        return (
            <div ref={containerRef} id={`tradingview-chart-${symbol}`} />
        );
    };

    const RowSignInPopup = ({ index, onClose }: any) => {
        return (
            <div className="inline-block bg-white p-6 rounded-lg shadow-lg text-center">
                <p className="text-lg font-semibold mb-4">
                    Please Sign In to unlock real-time data.
                </p>
                <button
                    onClick={() => onClose(index)}
                    className="mt-2 py-2 px-4 text-xs lg:text-base text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight transition"
                >
                    Okay
                </button>
            </div>
        );
    };

    const disableFutureAndToday = (current: any) => {
        if (isGuestUser.current) {
            return current && current >= dayjs().startOf('day');
        }
        return false;
    };

    useEffect(() => {
        if (!isGuestUser.current) {
            setScrollHeight(isFilterOpen ?
                "h-[calc(100vh-315px)] xl:h-[calc(100vh-245px)]" :
                `h-[calc(100vh-270px)] ${!isAuthenticated.current ? "xl:h-[calc(100vh-215px)]" : "xl:h-[calc(100vh-145px)]"}`
            );
        } else {
            setScrollHeight(isFilterOpen ?
                "h-[calc(100vh-450px)] md:h-[calc(100vh-315px)] xl:h-[calc(100vh-305px)]" :
                `h-[calc(100vh-270px)] ${!isAuthenticated.current ? "xl:h-[calc(100vh-215px)]" : "xl:h-[calc(100vh-145px)]"}`
            );
        }
    }, [isGuestUser.current, isFilterOpen, isAuthenticated.current]);

    return (
        <div className='mb-2.5'>
            <div className='p-2.5 lg:p-4 rounded-xl shadow-box'>
                {/* <div>
                    <h2 className='text-[17px] hidden 2xl:block lg:text-[21px] font-bold text-black text-start mb-2'>AI Trade Recommendation</h2>
                </div> */}
                <div className="mb-2">
                    <div className='flex items-center gap-2 ml-7 sm:ml-0'>
                        {/* Filter Button - Visible only on mobile */}
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)} // Toggle the filter section
                            className="py-1.5 px-2 lg:py-2 lg:px-4 text-black border border-blue-midnight rounded-md flex items-center justify-between gap-1.5 w-full"
                        >
                            Filter
                            {/* Conditional Icon Rendering */}
                            {isFilterOpen ? (
                                <DownCircleOutlined className="text-blue-midnight" />
                            ) : (
                                <LeftCircleOutlined className="text-blue-midnight" />
                            )}
                        </button>
                        <div>
                            <button
                                onClick={() => {
                                    onRefresh();
                                }}
                                className="py-2.5 px-2 lg:px-4 text-xs lg:text-sm lg:py-2 text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight flex items-center gap-1.5"
                            >
                                <ReloadOutlined />
                                {loading ? "Loading..." : "Refresh"}
                            </button>
                        </div>
                    </div>
                    {isFilterOpen && (
                        <div className='block md:items-center md:flex gap-4 w-full justify-end border rounded-lg shadow-md mt-4 p-2 sm:p-3.5'>
                            <div className='lg:flex gap-4 items-center w-full justify-end'>
                                <div>
                                    <Search placeholder="Search Contract Name" allowClear onChange={onSearchContractName} className='w-full text-start mit-searchbar mb-2 lg:mb-0' />
                                </div>
                                <div>
                                    <Space direction="vertical" size={12} className='w-full mb-2 lg:mb-0'>
                                        <RangePicker
                                            onChange={handleDateChange}
                                            className='p-2 w-full min-w-80 md:max-w-[600px] datepicker-table'
                                            disabledDate={disableFutureAndToday}
                                        />
                                    </Space>
                                </div>
                                <div>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        className='w-full min-w-80 md:max-w-[600px] text-start multi-user-dropdown'
                                        placeholder="Select Stock Symbol" //"Select Companies"
                                        onChange={handleStockSymbolChange}
                                        options={stocks}
                                        open={stockSymbolOpen} // Control dropdown visibility
                                        onDropdownVisibleChange={(visible) => setStockSymbolOpen(visible)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="overflow-auto">
                    {loading ? (
                        <div className="space-y-4">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="animate-pulse flex w-full">
                                    <div className="w-1/12 h-6 bg-gray-300 rounded hidden lg:block"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                </div>
                            ))}
                        </div>
                    ) : articleStockRecommendationData.length === 0 ? (
                        <div>
                            <p>No records found</p>
                        </div>
                    ) : (
                        <div className={`overflow-x-auto mt-1.5 ${scrollHeight}`}>
                            <table className="min-w-full w-full">
                                <thead className='sticky top-0 z-10'>
                                    <tr className='bg-[#000639] text-white text-xs lg:text-sm'>
                                        <th className=" py-3 md:py-4 px-3.5 text-left whitespace-nowrap"></th>
                                        <th className="hidden lg:block py-3 md:py-4 px-3.5 text-left whitespace-nowrap">No.</th>
                                        <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-left whitespace-nowrap min-w-[275px] md:w-[370px] xl:min-w-auto">Contract Name</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>At</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Trade Side</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Price</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Ticker</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articleStockRecommendationData.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <tr
                                                className={`border-b border-gray-700`}
                                                style={{ cursor: 'pointer' }} // Make it visually clear that it's clickable
                                            >
                                                <td onClick={() => handleRowClick(index)} >

                                                    <button
                                                        onClick={() => handleRowClick(index)}
                                                        className="text-sm font-bold flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-md transition-all duration-300 collapse-icon"
                                                    >
                                                        {expandedRowIndex === index ? <MinusOutlined /> : <PlusOutlined />}
                                                    </button>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 text-left hidden lg:block'>{item.rowNumber}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-start'>
                                                    <div className='flex items-center gap-4'>
                                                        <div className={`${'text-black-500'} `}>
                                                            {item.contractName}
                                                        </div>
                                                        <div>
                                                            <div className='flex items-center gap-1'>
                                                                {((item.yahooFinanceStockSymbol || "") !== "") ?
                                                                    <a href={`https://finance.yahoo.com/quote/${item.yahooFinanceStockSymbol}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer">
                                                                        <img src={Images.yahooicon} className='w-5' alt="Yahoo" />
                                                                    </a> : <></>
                                                                }
                                                                {((item.tradingViewStockSymbol || "") !== "") ?
                                                                    <a href={`https://tradingview.com/chart?symbol=${item.tradingViewStockSymbol}&interval=1D`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer">
                                                                        <img src={Images.tradingicon} className='w-5' alt="Trading" />
                                                                    </a> : <></>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap relative group text-center">
                                                    <span>{item.createdAtAgo}</span>
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-3/4 lg:transform-translate-x-2/3 mb-2 hidden group-hover:block bg-blue-midnight text-white text-xs rounded px-2 py-1 z-10">
                                                        {new Date(item.createdAt).toLocaleString('en-GB', {
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true,
                                                        })}
                                                    </div>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <span className={`px-1.5 lg:px-2 py-0.5 lg:py-1 rounded ${item.tradeSide === 'BUY' ? 'bg-green-mountain-meadow bg-opacity-60 border-green-mountain-meadow border  text-black' : 'bg-red-persian bg-opacity-60 text-white border-red-persian border'}`}>
                                                        {item.tradeSide}
                                                    </span>
                                                </td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>${formatPrice(item.lastPrice)}</td>
                                                <td className='p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center'>
                                                    <a
                                                        href={`https://tradingview.com/chart?symbol=${item.parentStockSymbol}&interval=1D`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-500 hover:underline"
                                                    >
                                                        {item.parentStockSymbol} (${formatNumberWithCommas(item.spotLastPrice)})
                                                    </a></td>
                                                <td className="p-2.5 text-xs lg:text-sm lg:p-3.5 whitespace-nowrap text-center">
                                                    <EyeOutlined
                                                        onClick={() => handleOnClickArticleContent(item.articleId)} // Handle click
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </td>
                                            </tr>
                                            {expandedRowIndex === index && (
                                                <tr className="bg-gray-100 relative">
                                                    <td colSpan={12} className="p-4 text-left">
                                                        <div className={`${isGuestUser.current ? 'blur-md pointer-events-none' : ''}`}>
                                                            {/* Left side: Radio buttons for Spot and Option */}
                                                            <div className="flex items-center gap-4">
                                                                <div className="flex items-center cursor-pointer">
                                                                    <input
                                                                        type="radio"
                                                                        id={`spot-${index}`}
                                                                        name={`contractType-${index}`}
                                                                        value="spot"
                                                                        className="mr-2 cursor-pointer"
                                                                        checked={selectedTradingType === 'spot'}
                                                                        onChange={() => handleTradingTypeChange('spot')}
                                                                    />
                                                                    <label htmlFor={`spot-${index}`} className={`text-sm ${selectedTradingType === 'spot' ? 'text-blue-midnight font-bold' : 'text-gray-700'
                                                                        }`}>Spot</label>
                                                                </div>
                                                                <div className="flex items-center cursor-pointer">
                                                                    <input
                                                                        type="radio"
                                                                        id={`option-${index}`}
                                                                        name={`contractType-${index}`}
                                                                        value="option"
                                                                        className="mr-2 cursor-pointer"
                                                                        checked={selectedTradingType === 'option'}
                                                                        onChange={() => handleTradingTypeChange('option')}
                                                                    />
                                                                    <label htmlFor={`option-${index}`} className={`text-sm ${selectedTradingType === 'option' ? 'text-blue-midnight font-bold' : 'text-gray-700'
                                                                        }`}>Option</label>
                                                                </div>
                                                                {/* Additional buttons under 'Option' */}
                                                                {/* {selectedTradingType === 'option' && (
                                                                    <div className="flex flex-col space-y-2 ml-2">
                                                                        <div className="flex items-center">
                                                                            <input
                                                                                type="radio"
                                                                                id={`tradeview-${index}`}
                                                                                name={`optionType-${index}`}
                                                                                value="tradeview"
                                                                                checked={optionTradeSelectedType === 'tradeview'}
                                                                                onChange={() => handleOptionTradeChartChange('tradeview')}
                                                                            />
                                                                            <label htmlFor={`tradeview-${index}`} className="text-sm">TradingView</label>
                                                                        </div>
                                                                        <div className="flex items-center">
                                                                            <input
                                                                                type="radio"
                                                                                id={`yahoo-${index}`}
                                                                                name={`optionType-${index}`}
                                                                                value="yahoo"
                                                                                checked={optionTradeSelectedType === 'yahoo'}
                                                                                onChange={() => handleOptionTradeChartChange('yahoo')}
                                                                            />
                                                                            <label htmlFor={`yahoo-${index}`} className="text-sm">Yahoo Finance</label>
                                                                        </div>
                                                                    </div>
                                                                )} */}
                                                            </div>
                                                            {/* Right side: TradingView Chart */}
                                                            <div className="flex-1 ml-4">
                                                                <div className="mt-4">
                                                                    {/* Render the TradingView chart or Yahoo Finance iframe based on selected options */}

                                                                    <TradingViewChart symbol={selectedTradingType === 'spot' ? articleStockRecommendationData[index]?.parentStockSymbol :
                                                                        selectedTradingType === 'option' ? articleStockRecommendationData[index]?.tradingViewStockSymbol : "SAVA"
                                                                    } />

                                                                    {/* {selectedTradingType === 'spot' && (
                                                                        <TradingViewChart symbol={data[index]?.parentStockSymbol} />
                                                                    )} */}
                                                                    {/* 
                                                                    {selectedTradingType === 'option' && optionTradeSelectedType === 'tradeview' && (
                                                                        <TradingViewChart symbol={data[index]?.tradingViewStockSymbol} />
                                                                    )} */}

                                                                    {/* {selectedTradingType === 'option' && optionTradeSelectedType === 'yahoo' && (
                                                                        <YahooFinance url={`https://finance.yahoo.com/chart/${item.yahooFinanceStockSymbol}`} />
                                                                    )} */}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {showSignInPopup && (
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <RowSignInPopup index={index} onClose={handleRowClick} />
                                                        </div>
                                                    )}
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <Modal
                                    title="Article Summary"
                                    visible={isModalVisible}
                                    onOk={closeModal}
                                    onCancel={closeModal}
                                    footer={null}
                                    centered
                                    width={1800}
                                    height={850}
                                >
                                    <div dangerouslySetInnerHTML={{ __html: articleContent }} />
                                </Modal>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <Pagination
                total={totalItems}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                className='mt-2.5 md:px-6 pagination-table'
                showSizeChanger
                align='end'
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
        </div>
    );
};

export default ParadigmEvents;